/**
 * CJdropshipping vs Spocket 子页面 — 页面常量（全量文案）
 * 路由：/cjdropshipping-vs-spocket
 *
 * 【注意】
 * - Spocket 是一个以欧美供应商为主的 dropshipping 平台（产品来自美国/欧洲，快速发货）
 * - 图片使用 CDN 路径（resources/egg/alternatives/），函数接受 cdnHost 参数
 * - TESTIMONIALS/SELLPOINTS/FIT/ADVANTAGES/BOTTOM_CTA 与主页相同，走公共组件
 */

/**
 * 【注意】图片 CDN 路径：${cdnHost}/resources/egg/alternatives/filename
 * 参照 odm/index.jsx 模式，接受 cdnHost 参数
 */
export function getSpocketConstants(cdnHost) {
  const img = (name) => `${cdnHost || ''}/resources/egg/alternatives/${name}`;
  return {
    // ========== VS_HERO: 子页 Banner ==========
    VS_HERO: {
      eyebrow: 'Spocket Alternative',
      title: 'CJdropshipping vs',
      titleHighlight: 'Spocket',
      subtitle: 'Looking for a Better Dropshipping Spocket Alternative?',
      desc: 'CJdropshipping brings product sourcing, fulfillment, warehousing, POD, custom packaging, and branding together to help you grow faster and scale smarter.',
      proofItems: ['Global sourcing', 'Quality inspection', 'Custom packaging'],
      ctaText: 'Try for free',
      ctaUrl: '/home',
      note: 'Built for sellers who want supply chain flexibility and better margins.',
      heroImage: img('hero_banner5.jpg'),
      topChipText: 'Global supply chain support',
      bottomCard: {
        label: 'Why CJdropshipping',
        title: 'More than US/EU suppliers — a complete dropshipping operation',
        desc: 'Reliable sourcing, quality inspection, faster fulfillment, and branding support — all in one operational workflow.',
      },
    },

    // ========== SELLPOINTS: 卖点标签 ==========
    SELLPOINTS: [
      'Product Sourcing',
      'Global Fulfillment',
      'Quality Inspection',
      'Custom Packaging',
      'Print on Demand',
      'Global Warehouses',
      'Dedicated Agent Support',
    ],

    // ========== VS_TABLE: 三列对比表 ==========
    VS_TABLE: {
      kicker: 'Platform Comparison',
      title: '<strong>CJdropshipping</strong> vs Spocket: Key Advantages at a Glance',
      desc: 'CJdropshipping offers more flexible global sourcing with competitive pricing, quality inspection, custom branding, and fulfillment support — going beyond Spocket\'s US/EU supplier network.',
      summaryCards: [
        {
          icon: '1',
          title: 'More than suppliers',
          desc: 'Spocket connects sellers with suppliers, while CJdropshipping supports the full dropshipping workflow from sourcing to fulfillment.',
        },
        {
          icon: '2',
          title: 'Built for flexibility',
          desc: 'CJdropshipping gives sellers sourcing, inspection, warehousing, shipping, POD, and packaging support in one platform.',
        },
        {
          icon: '3',
          title: 'Better for scaling',
          desc: 'CJdropshipping is stronger for sellers who want more product options, custom branding, and better operational control.',
        },
      ],
      headerFeature: 'Feature',
      headerCompetitor: 'Spocket',
      headerCj: 'CJdropshipping',
      rows: [
        {
          feature: 'Overall Positioning',
          competitor: 'A supplier marketplace mainly focused on product discovery',
          cj: 'A complete sourcing, fulfillment, warehousing, POD, and branding platform',
        },
        {
          feature: 'Product Sourcing',
          competitor: 'Sellers mainly choose from existing supplier catalog products',
          cj: 'Helps sellers source products based on their niche, target market, and business needs',
        },
        {
          feature: 'Product Flexibility',
          competitor: 'More limited to products available in the supplier network',
          cj: 'Greater flexibility to source, test, customize, and scale different product types',
        },
        {
          feature: 'Fulfillment Control',
          competitor: 'Fulfillment depends more on individual suppliers',
          cj: 'Supports order processing, packing, shipping, warehousing, and fulfillment workflows',
        },
        {
          feature: 'Warehousing',
          competitor: 'Less focused on warehousing infrastructure',
          cj: 'Offers global warehousing and 3PL fulfillment support',
        },
        {
          feature: 'Custom Packaging',
          competitor: 'More limited branding options, mainly supplier-dependent',
          cj: 'Supports custom packaging and branding services',
        },
        {
          feature: 'POD & Customization',
          competitor: 'Not the core strength',
          cj: 'Supports POD and customized product opportunities',
        },
        {
          feature: 'Brand Building',
          competitor: 'Better suited for sellers who want quick access to ready-to-sell products',
          cj: 'Better suited for sellers who want to create a branded ecommerce experience',
        },
        {
          feature: 'Scaling Potential',
          competitor: 'Better for early-stage product browsing and simple dropshipping workflows',
          cj: 'Stronger for sellers moving from product testing to long-term business growth',
        },
        {
          feature: 'Best Fit',
          competitor: 'Sellers who prioritize browsing US/EU supplier products',
          cj: 'Sellers who want supply chain control, fulfillment support, and brand growth',
        },
      ],
    },

    // ========== FIT: Who Should Choose CJdropshipping ==========
    FIT: {
      kicker: 'Seller Fit Guide',
      title: 'Who Should Choose <strong>CJdropshipping?</strong>',
      desc: 'CJdropshipping is a better fit for sellers who:',
      imageUrl: img('fit_who_should_choose.jpg'),
      badgeText: '✓ Best Fit for Growth Sellers',
      floatTitle: 'Brand-building ecommerce sellers',
      floatDesc: 'A stronger fit for merchants who want global sourcing, better pricing, branding, and long-term operational support.',
      items: [
        'Need reliable product sourcing',
        'Want fulfillment support for dropshipping orders',
        'Sell through Shopify, WooCommerce, TikTok Shop, or eBay',
        'Want to test or sell POD products',
        'Need custom packaging or branding services',
        'Want to improve shipping and customer experience',
        'Plan to move from marketplace reselling to brand-based ecommerce',
        'See dropshipping as a long-term business, not just short-term product testing',
      ],
      summaryTitle: 'Why CJdropshipping stands out',
      summaryDesc: 'CJdropshipping is ideal for sellers who want to go beyond simple product copying. It helps you build a stronger business foundation through sourcing, fulfillment, customization, and branding support.',
    },

    // ========== ADVANTAGES: 核心优势 ==========
    ADVANTAGES: {
      kicker: 'Competitive Advantages',
      title: 'How <strong>CJdropshipping</strong> beat the competition',
      desc: 'CJdropshipping is a full dropshipping supply chain partner. It helps sellers go beyond product listing with sourcing, fulfillment, shipping, branding, and dedicated support.',
      cards: [
        {
          num: '01',
          title: 'Full Dropshipping Supply Chain Support',
          desc: 'CJdropshipping covers more than listing. It supports product sourcing, procurement, warehousing, fulfillment, shipping, and after-sales workflows.',
          imageUrl: img('adv_supply_chain.jpg'),
        },
        {
          num: '02',
          title: 'Reliable Product Sourcing',
          desc: 'Sellers can submit sourcing requests and find products through CJdropshipping\'s supplier resources, making it easier to discover better products and improve margins.',
          imageUrl: img('adv_sourcing.jpg'),
        },
        {
          num: '03',
          title: 'Quality Inspection Before Shipping',
          desc: 'CJdropshipping helps inspect products and packages before shipment, reducing the risk of defects, complaints, refunds, and negative reviews.',
          imageUrl: img('adv_quality_inspection.jpg'),
        },
        {
          num: '04',
          title: 'Global Warehousing',
          desc: 'With warehouse support in different markets, CJdropshipping helps sellers store inventory closer to customers and improve fulfillment efficiency.',
          imageUrl: img('adv_warehousing.jpg'),
        },
        {
          num: '05',
          title: 'End-to-End Order Fulfillment',
          desc: 'CJdropshipping can handle order processing, picking, packing, shipping, tracking, and delivery support, helping sellers reduce manual work.',
          imageUrl: img('adv_fulfillment.jpg'),
        },
        {
          num: '06',
          title: 'Global Shipping Solutions',
          desc: 'CJdropshipping provides global logistics options to help sellers deliver products to customers more efficiently and reliably.',
          imageUrl: img('adv_shipping.jpg'),
        },
        {
          num: '07',
          title: 'Custom Packaging and Branding',
          desc: 'Sellers can create branded packaging, inserts, labels, and unboxing experiences, making it easier to build a real ecommerce brand.',
          imageUrl: img('adv_custom_packaging.jpg'),
        },
        {
          num: '08',
          title: 'Dedicated Agent Support',
          desc: 'CJdropshipping provides service support to help sellers with sourcing, fulfillment, logistics, and operational issues as their business grows.',
          imageUrl: img('adv_dedicated_support.jpg'),
        },
      ],
      footerCta: {
        text: 'CJdropshipping gives sellers the supply chain flexibility and operational support needed to source globally, fulfill reliably, brand professionally, and scale with better margins.',
        ctaText: 'GET STARTED',
        ctaUrl: '/home',
      },
    },

    // ========== TESTIMONIALS: 买家评价 ==========
    TESTIMONIALS: {
      kicker: 'Real Seller Stories',
      title: 'Why Growing Sellers <strong>Stay with CJdropshipping</strong>',
      desc: 'Hear how ecommerce sellers use CJdropshipping to streamline sourcing, fulfillment, branding, and shipping while building stronger long-term businesses.',
      rows: [
        [
          {
            avatarUrl: img('32.jpeg'),
            name: 'Emily Carter',
            role: 'Shopify Store Owner',
            country: 'United States',
            text: 'CJdropshipping helped me simplify my fulfillment process. Instead of manually coordinating with multiple suppliers, I can manage sourcing, orders, and shipping in one place. It saves me a lot of time every week.',
          },
          {
            avatarUrl: img('12.jpeg'),
            name: 'Daniel Hughes',
            role: 'Ecommerce Entrepreneur',
            country: 'United Kingdom',
            text: 'As my store started growing, I needed more than just a product listing tool. CJdropshipping gave me access to sourcing, fulfillment, and packaging support, which made it easier to scale my business.',
          },
          {
            avatarUrl: img('47.jpeg'),
            name: 'Sofia Martinez',
            role: 'Dropshipping Seller',
            country: 'Spain',
            text: 'One of the biggest advantages for me is product sourcing. When I cannot find the right product, CJdropshipping helps me look for alternatives and connect with supply options that fit my store.',
          },
        ],
        [
          {
            avatarUrl: img('15.jpeg'),
            name: 'Liam Walker',
            role: 'DTC Brand Founder',
            country: 'Australia',
            text: 'I wanted my store to feel more professional, and CJdropshipping\'s custom packaging and branding options helped a lot. It made my customer experience feel less generic.',
          },
          {
            avatarUrl: img('5.jpeg'),
            name: 'Chloe Anderson',
            role: 'Online Store Operator',
            country: 'Canada',
            text: 'Managing products, orders, and fulfillment through one platform makes daily operations much easier. CJdropshipping is especially helpful if you want to reduce repetitive manual work.',
          },
          {
            avatarUrl: img('60.jpeg'),
            name: 'Marco Rossi',
            role: 'Ecommerce Seller',
            country: 'Italy',
            text: 'CJdropshipping feels like a better fit for sellers who want to build a serious dropshipping business. It is not only about listing products, but also about improving sourcing, shipping, and the overall customer experience.',
          },
        ],
      ],
    },

    // ========== VS_FAQ: 子页专属 FAQ ==========
    VS_FAQ: {
      kicker: 'FAQ',
      title: 'Spocket vs <strong>CJdropshipping</strong> FAQ',
      intro:
        'Get quick answers about how CJdropshipping compares with Spocket for sourcing, pricing, fulfillment, shipping, branding, and long-term dropshipping growth.',
      sideCard: {
        label: 'Quick Decision Guide',
        title: 'Need more than US/EU supplier products?',
        desc: 'Spocket specializes in US and EU suppliers for faster domestic shipping, while CJdropshipping gives sellers broader global sourcing, better pricing, inspection, fulfillment, packaging, and brand-building support.',
        miniItems: [
          { text: 'More competitive product pricing' },
          { text: 'Quality inspection before shipping' },
          { text: 'Custom packaging and branding support' },
        ],
      },
      items: [
        {
          q: 'Is CJdropshipping a Spocket alternative?',
          a: 'Yes. CJdropshipping can be a Spocket alternative if your needs go beyond browsing supplier products. It is better suited for sellers who need sourcing, fulfillment, warehousing, POD, custom packaging, 3PL fulfillment, and branding support.',
        },
        {
          q: 'What is the biggest difference between CJdropshipping and Spocket?',
          a: 'Spocket focuses more on helping sellers discover and import products from supplier catalogs, especially US and EU suppliers. CJdropshipping focuses more on flexible sourcing, fulfillment, warehousing, POD, custom packaging, and supply chain support.',
        },
        {
          q: 'Is CJdropshipping better for custom packaging?',
          a: 'CJdropshipping is strongly aligned with custom packaging needs. Its custom packaging service includes branding elements such as logos, brand colors, and unique designs to help improve brand recognition and customer loyalty.',
        },
        {
          q: 'Which one is better for Shopify sellers?',
          a: 'Spocket can be useful for Shopify sellers who want supplier catalog access and US/EU products. CJdropshipping is usually a better fit for Shopify sellers who need flexible sourcing, fulfillment, custom packaging, POD, and brand-building support.',
        },
        {
          q: 'Which one is better for fulfillment?',
          a: 'CJdropshipping is more fulfillment-oriented because it supports order processing, warehousing, packing, shipping, and 3PL fulfillment. Spocket also supports order fulfillment, but it is more supplier-network and catalog focused.',
        },
        {
          q: 'Who should choose CJdropshipping instead of Spocket?',
          a: 'CJdropshipping is better for sellers who want more control over product sourcing, fulfillment, warehousing, packaging, and brand experience. It is especially suitable for sellers who want to scale beyond basic supplier catalog dropshipping.',
        },
      ],
      bottomText: 'Still have questions? Start with a free account and explore what CJdropshipping can do for your business.',
      bottomCtaText: 'Try CJdropshipping Free',
      bottomCtaUrl: '/home',
    },

    // ========== BOTTOM_CTA: 底部行动召唤 ==========
    BOTTOM_CTA: {
      kicker: 'Ready to Scale Smarter?',
      title: 'Ready to move from supplier browsing to complete sourcing and fulfillment?',
      desc: 'Start with CJdropshipping and build a more scalable ecommerce business.',
      ctaText: 'Upgrade from Spocket to CJdropshipping',
      ctaUrl: '/home',
      bgImage: img('hero_banner.jpg'),
    },
  };
}
